import type { ReauthKind } from '@felix/domain';
import { Button, Group } from '@mantine/core';
import { IconArrowBackUp, IconCheck, IconEraser, IconPencil } from '@tabler/icons-react';
import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import work from './PmsWork.module.css';
import { PmsBadge, PmsPasswordDialog } from './PmsWorkParts.js';
import type { DecisionInput, HistoryEntry } from './types.js';

/**
 * Onay şeridinin konusu, kayıt türünden bağımsız (M8b3; plan M8b Task 18): PMS iş kaydı
 * (`WorkOrderRecord`) ve ISM form kaydı aynı şeridi kullanır. Karar ekranda görülen gönderim,
 * adım ve içerik hash'iyle verilir (bayat karar yazılmaz; mimari belge §7.3).
 */
export interface ApprovalSubject {
  status: string;
  contentHash: string;
  submissionId: string | null;
  step: number | null;
  office: boolean;
}

export interface ApprovalPanelProps {
  record: ApprovalSubject;
  entry: Pick<HistoryEntry, 'createdByName' | 'createdAt'> | null;
  /** Kişi bu adımın onaylayıcısı (gemide zildeki madde; K8). */
  canDecide: boolean;
  /** Kişinin kendi kaydı ya da iptal yetkisi (gemide; K14). */
  canVoid: boolean;
  onDecide: (input: DecisionInput) => Promise<void>;
  onVoid: () => void;
  onCorrect: () => void;
  /** Kartın başlığı (varsayılan PMS: "Chief Engineer Approval"). */
  title?: string;
  /** Durum rozeti (varsayılan PMS durumu). */
  badge?: ReactNode;
  /**
   * Bekleyen adımın süresi geçti: onayda da açıklama zorunludur ve karar gerekçesine yazılır
   * (`LATE_REASON_REQUIRED`; ADR 0042 §16) [MEPC.312 4.3.10'dan esinlenen ajan kararı].
   */
  overdue?: boolean;
  /** Parola penceresinde kararın konusu ("ISM form record FR-…"). */
  subjectName?: string;
  /**
   * Kaydın onay türü (ADR 0075): parola alanı yalnız şirket ayarı açıkken görünür. Verilmezse PMS
   * iş kaydı (`pms_work_record`).
   */
  reauthKind?: ReauthKind;
  /** Hata metni (varsayılan PMS metinleri). */
  errorText?: (error: unknown) => string;
  /** Rozetin altındaki bilgi (bekleyen rol ve süre, karar izi). */
  children?: ReactNode;
  /**
   * Karar şimdilik verilemez ve nedeni (ofis adımında dosyalar buluta ulaşmadı:
   * `FILE_NOT_UPLOADED`; ADR 0042 §15): Approve ve Return pasif, neden yazılı.
   */
  blockedReason?: string | null;
}

/**
 * Kararın gerekçesi: iade her zaman gerekçe ister (`RETURN_REASON_REQUIRED`); süresi geçmiş adımda
 * onay da açıklama ister (`LATE_REASON_REQUIRED`; ADR 0042 §16); değilse onay yorumu isteğe bağlı.
 */
export function decisionReasonMode(
  action: 'approved' | 'returned' | null,
  overdue: boolean,
): 'required' | 'optional' {
  return action === 'returned' || overdue ? 'required' : 'optional';
}

/**
 * Onay şeridi (K8; ADR 0042 §16): kaydın onay durumu; onaylayıcıya Approve ve Return, ikisi de
 * parolayla (kapatılamaz; mimari belge §7). PMS'te kaydın sahibine Void ve Correct (K14):
 * raporlanmış kayıt değişmez, düzeltme gerekçeli iptal ve yeni kayıtla [NR496 4.5.3f].
 */
export function ApprovalPanel({
  record,
  entry,
  canDecide,
  canVoid,
  onDecide,
  onVoid,
  onCorrect,
  title,
  badge,
  overdue = false,
  subjectName,
  errorText,
  children,
  blockedReason = null,
  reauthKind = 'pms_work_record',
}: ApprovalPanelProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.work.approval.${key}`, values);
  const [action, setAction] = useState<'approved' | 'returned' | null>(null);
  const blockedId = useId();
  const pending = record.status === 'pending' || record.status === 'draft';
  const reasonMode = decisionReasonMode(action, overdue);
  return (
    <Panel title={title ?? k('title')}>
      <div className={work.drawerStack}>
        <div className={work.badgeRow}>
          {badge ?? (
            <PmsBadge status={pending ? 'pending' : record.status}>
              {pending
                ? record.office
                  ? k('officeStep', { step: record.step ?? 1 })
                  : k('pending', { step: record.step ?? 1 })
                : k(record.status)}
            </PmsBadge>
          )}
        </div>
        {entry ? (
          <p className={work.note}>
            {k('reportedBy', {
              name: entry.createdByName ?? '—',
              at: formatDateTime(entry.createdAt),
            })}
          </p>
        ) : null}
        {children}
        {canDecide && pending ? (
          <Group gap="sm">
            <Button
              leftSection={<IconCheck size={16} />}
              disabled={blockedReason !== null}
              aria-describedby={blockedReason ? blockedId : undefined}
              onClick={() => setAction('approved')}
            >
              {t('pms.work.decision.approve')}
            </Button>
            <Button
              variant="default"
              leftSection={<IconArrowBackUp size={16} />}
              disabled={blockedReason !== null}
              aria-describedby={blockedReason ? blockedId : undefined}
              onClick={() => setAction('returned')}
            >
              {t('pms.work.decision.return')}
            </Button>
          </Group>
        ) : null}
        {canDecide && pending && blockedReason ? (
          <p id={blockedId} className={work.note} role="note">
            {blockedReason}
          </p>
        ) : null}
        {canVoid ? (
          <Group gap="sm">
            <Button variant="default" leftSection={<IconPencil size={16} />} onClick={onCorrect}>
              {t('pms.work.void.correct')}
            </Button>
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconEraser size={16} />}
              onClick={onVoid}
            >
              {t('pms.work.void.void')}
            </Button>
          </Group>
        ) : null}
      </div>
      <PmsPasswordDialog
        opened={action !== null}
        title={
          action === 'returned'
            ? t('pms.work.decision.returnTitle')
            : t('pms.work.decision.approveTitle')
        }
        body={t(
          action === 'returned' ? 'pms.work.decision.returnBody' : 'pms.work.decision.approveBody',
          { name: subjectName ?? t('pms.work.decision.title.record') },
        )}
        reason={reasonMode}
        {...(overdue ? { reasonLabel: t('forms.decision.lateReason') } : {})}
        {...(errorText ? { errorText, reasonMaxLength: 2000 } : {})}
        confirmLabel={
          action === 'returned' ? t('pms.work.decision.return') : t('pms.work.decision.approve')
        }
        danger={action === 'returned'}
        onClose={() => setAction(null)}
        reauthKind={reauthKind}
        onConfirm={async ({ reason, password }) => {
          if (!action || !record.submissionId || record.step === null) return null;
          await onDecide({
            contentHash: record.contentHash,
            submissionId: record.submissionId,
            step: record.step,
            decision: action,
            ...(reason ? { reason } : {}),
            ...(password ? { password } : {}),
          });
          setAction(null);
          return null;
        }}
      />
    </Panel>
  );
}
